import { FC, Fragment, useCallback, useEffect, useMemo, useState } from "react";
import Input from "../../../../components/Form/El/Input";
import { ApolloError, useMutation } from "@apollo/client";
import {
  CREATE_QUICK_QUOTE_LINE,
  UPDATE_QUICK_QUOTE_LINE,
} from "../../Services/Mutations/Mutaions";
import { DataCollectionItem } from "../../../../types/AdminDashboard";
import { isValidInteger } from "../../../../helpers/Validation";
import { getIntegerNumberFromString } from "../../../../helpers/utils";

const AdminDashboardEditQuickQuoteLine: FC<{
  currentId?: number;
  isNeedSave: boolean;
  afterSave: (id: number) => void;
  labelWidth: string;
  inputWidth: string;
  currentDataCollectionItem?: DataCollectionItem;
  setIsSaveLoading: (state: boolean) => void;
  setDisabledSaveButton: (state: boolean) => void;
  onErrorSave: (error: ApolloError) => void;
}> = ({
  labelWidth,
  currentDataCollectionItem,
  inputWidth,
  isNeedSave,
  currentId,
  afterSave,
  setIsSaveLoading,
  setDisabledSaveButton,
  onErrorSave,
}) => {
  const clearForm = useCallback(() => {
    setCurrentDescription("");
    setCurrentPrice(null);
    setCurrentCount(null);
    setCurrentName("");
  }, []);

  const onAfterSave = useCallback((id: number) => {
    clearForm();
    afterSave(id || 0);
  }, []);

  const [updateItems] = useMutation(UPDATE_QUICK_QUOTE_LINE, {
    onError: (error) => {
      onErrorSave(error);
    },
    onCompleted: (data) => {
      const { updateQuickQuoteLine } = data;

      if (!updateQuickQuoteLine) return;

      const { id } = updateQuickQuoteLine;

      onAfterSave(id || 0);
    },
  });
  const [createItems] = useMutation(CREATE_QUICK_QUOTE_LINE, {
    onError: (error) => {
      onErrorSave(error);
    },
    onCompleted: (data) => {
      const { createQuickQuoteLine } = data;

      if (!createQuickQuoteLine) return;

      const { id } = createQuickQuoteLine;

      onAfterSave(id || 0);
    },
  });

  const [currentDescription, setCurrentDescription] = useState("");
  const [currentName, setCurrentName] = useState("");
  const [currentPrice, setCurrentPrice] = useState<string | null>(null);
  const [currentCount, setCurrentCount] = useState<string | null>(null);

  const isValidPrice = useMemo(() => {
    return currentPrice === null || isValidInteger(currentPrice);
  }, [currentPrice]);

  const isValidCount = useMemo(() => {
    return currentCount === null || isValidInteger(currentCount);
  }, [currentCount]);

  useEffect(() => {
    if (!currentDataCollectionItem) return;

    const { description, price, count, name } = currentDataCollectionItem;

    if (description) {
      setCurrentDescription(description);
    }

    if (typeof count === "number") {
      setCurrentCount(count.toString());
    }

    if (typeof price === "number") {
      setCurrentPrice(price.toString());
    }

    if (name) {
      setCurrentName(name);
    }
  }, [currentDataCollectionItem]);

  const onSave = useCallback(async () => {
    setIsSaveLoading(true);
    const handleSaveById = async () => {
      if (currentId) {
        return await updateItems({
          variables: {
            id: currentId,
            description: currentDescription,
            count: getIntegerNumberFromString(currentCount),
            price: getIntegerNumberFromString(currentPrice),
            name: currentName,
          },
        });
      }
      return await createItems({
        variables: {
          description: currentDescription,
          count: getIntegerNumberFromString(currentCount),
          price: getIntegerNumberFromString(currentPrice),
          name: currentName,
        },
      });
    };

    await handleSaveById();
  }, [currentDescription, currentId, currentCount, currentPrice, currentName]);

  useEffect(() => {
    if (isNeedSave) {
      onSave();
    }
  }, [isNeedSave]);

  useEffect(() => {
    const isValidForm = !!currentName && isValidCount && isValidPrice;
    setDisabledSaveButton(!isValidForm);
  }, [currentName, isValidCount, isValidPrice]);

  return (
    <Fragment>
      <Input
        value={currentName}
        onChange={(value) => {
          if (typeof value === "string") {
            setCurrentName(value);
          }
        }}
        name="quick-qoute-line-name"
        labelWidth={labelWidth}
        label="Name"
        placeholder="Name"
        inputWidth={inputWidth}
        isIncorrectValue={!currentName}
      />
      <Input
        value={currentDescription}
        onChange={(value) => {
          if (typeof value === "string") {
            setCurrentDescription(value);
          }
        }}
        name="quick-qoute-line-dsc"
        labelWidth={labelWidth}
        label="Description"
        placeholder="Description"
        inputWidth={inputWidth}
      />
      <Input
        value={
          currentPrice !== null &&
          typeof +currentPrice === "number" &&
          !isNaN(+currentPrice)
            ? currentPrice
            : ""
        }
        onChange={(value) => {
          const stringValue = value.toString();
          setCurrentPrice(stringValue ? stringValue : null);
        }}
        name="quick-qoute-line-price"
        labelWidth={labelWidth}
        label="Price"
        placeholder="Price"
        inputWidth={inputWidth}
        type="number"
        isIncorrectValue={!isValidPrice}
      />
      <Input
        value={
          currentCount !== null &&
          typeof +currentCount === "number" &&
          !isNaN(+currentCount)
            ? currentCount
            : ""
        }
        onChange={(value) => {
          const stringValue = value.toString();
          setCurrentCount(stringValue ? stringValue : null);
        }}
        name="quick-qoute-line-count"
        labelWidth={labelWidth}
        label="Count"
        placeholder="Count"
        inputWidth={inputWidth}
        type="number"
        isIncorrectValue={!isValidCount}
      />
    </Fragment>
  );
};

export default AdminDashboardEditQuickQuoteLine;
